import {
  Dialog,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  StackProps,
  styled,
} from "@mui/material";
import { APP_ICONS } from "Assets/icons";
import {
  Checkbox,
  Img,
  Input,
  OpenTabLink,
  Row,
  Select,
  SmText,
  SmTextMd,
  TextMd,
  XsText,
} from "Components";
import { COLORS } from "constants/layout";
import { PATHS } from "constants/routes";
import useBreakpoints from "hooks/useBreakpoint";
import { ContentGenerationChip } from "Pages/send-inquiry/components";
import { useLoading } from "providers/loading";
import { useNotify } from "providers/notify";
import { Fragment, lazy, ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { apiCreateInquiry } from "services/api/rfq";
import { InquiryParamsType } from "types/inquiry";
import { ItemDataType } from "types/item";
import { InquiryFormType } from "types/params";
import { withLoading } from "hocs/withLoading";
import useUser from "hooks/useUser";
import { JoinUsForm } from "./";
import { useCommonData } from "providers/common-data";
import EmailInput from "Components/input/EmailInput";
import { SupplierType } from "types/supplier";
import { useTranslation } from "hooks/useTranslation";
import { SupplierInfors } from "Components/infos";
import { GreenButton } from "Components/button/index";
import { StyledStack } from "./SendInquiryForm.styles";
import BlockSpamInquiryPopup from "Components/popup/BlockSpamInquiryPopup";
import { ERROR_CODES } from "constants/code";

const FinishInquiryForm = withLoading(
  lazy(() => import("./FinishInquiryForm"))
);
const dataNotiSendInquiry = {
  INQUIRY_SENT_SUCCESSFULLY: 'INQUIRY_SENT_SUCCESSFULLY',
  INQUIRY_SENT_SUCCESSFULLY_WITH_CONTACT: 'INQUIRY_SENT_SUCCESSFULLY_WITH_CONTACT'
}
type Props = {
  supplierData?: SupplierType;
  itemData?: ItemDataType;
};

const SendInquiryForm = ({
  supplierData,
  itemData,
  ...rest
}: Props & StackProps) => {
  const t = useTranslation();
  const { user, isLoggedIn } = useUser();
  const { setNotify } = useNotify();
  const { setLoading, loading } = useLoading();
  const { isMobile } = useBreakpoints();
  const { name: farm_item_id } = itemData || {};
  const { farm_id } = supplierData || {};
  const { appConfigs } = useCommonData();

  const [inquiryId, setInquiryId] = useState<string>();
  const [acceptTerm, setAcceptTerm] = useState(true);
  const [successful, setSuccessful] = useState(false);
  const [oldContent, setOldContent] = useState<string | undefined>("");
  const [activateKey, setActivateKey] = useState<string | undefined>();
  const [blockDialog, setBlockDialog] = useState<{
    open: boolean;
    type?: string;
  }>({
    open: false,
    type: "",
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<InquiryFormType>({
    defaultValues: {
      email: "",
      content: "",
      auto_inquiry: true,
      customer_name: "",
      phone_number: "",
      country: "",
    },
    mode: "onChange",
  });

  const inputValue = watch("content");
  const phoneCountryCode = watch("country") || "";

  const countries = useMemo(
    () => appConfigs?.countries || [],
    [appConfigs?.countries]
  );

  const onChangeContent = (content: string) => {
    setValue("content", content);
    clearErrors("content");
  };

  const onHandleSubmit = async (formData: InquiryParamsType) => {
    try {
      const formattedData = {
        ...formData,
        farm_id,
        farm_item_id,
      };
      setLoading(true);
      const resData = await apiCreateInquiry(formattedData);
      const inquiryResData = resData?.data?.message?.data;
      const inquiryResError = resData?.data?.message?.error;
      if (inquiryResError) {
        if (
          [
            ERROR_CODES.REACH_OUT_LIMIT_REQUIRED_VERIFIED,
            ERROR_CODES.REACH_OUT_LIMIT,
          ].indexOf(inquiryResError) > -1
        ) {
          setBlockDialog({ open: true, type: inquiryResError });
        }
        return;
      }
      const activateKey = inquiryResData?.key;
      setNotify({
        open: true,
        type: "success",
        msg: t(`${dataNotiSendInquiry[resData?.data?.message?.msg as keyof typeof dataNotiSendInquiry]}`) || resData?.data?.message?.msg,
      });
      setValue("content", "");
      setOldContent(inputValue);
      setActivateKey(activateKey);
      setInquiryId(inquiryResData?.name);
      if (farm_item_id || (farm_id && activateKey)) {
        setSuccessful(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onCloseSuccessful = () => setSuccessful(false);

  const onCloseBlock = () => setBlockDialog({ open: false });

  const company = useMemo(() => {
    return {
      farmer_name: supplierData?.farmer_name,
      farm_avatar: supplierData?.farm_avatar,
      farm_id: supplierData?.farm_id,
      farm_name: supplierData?.farm_name,
      name: supplierData?.name,
      salutation: supplierData?.salutation,
      position: supplierData?.position,
    };
  }, [supplierData]);

  useEffect(() => {
    if (user?.is_logged_in) {
      reset({
        ...getValues(),
        email: user?.email,
        customer_name: user?.customer_name,
        country: user?.fd_country,
        phone_number: user?.customer_phone,
      });
    }
  }, [user?.is_logged_in]);

  return (
    <Fragment>
      <Stack
        component="form"
        onSubmit={handleSubmit(onHandleSubmit)}
        id="inquiry-form"
        // style={{ scrollMarginTop: 125 }}
        {...rest}
      >
        <StyledStack spacing={{ xs: 1, md: 2 }}>
          <TextMd>{t("send_direct_inquiry")}</TextMd>

          {/* Supplier informations */}
          <Row spacing={1}>
            <SmTextMd>{t("to")}</SmTextMd>
            <SupplierInfors company={company} />
          </Row>

          <Input
            className="input content-input"
            multiline
            toplabel={t("message")}
            minRows={4}
            maxRows={6}
            {...register("content", {
              required: t("requied_input"),
            })}
            InputProps={{
              endAdornment: !inputValue && (
                <ContentGenerationChip
                  genNumber={isMobile ? 2 : 5}
                  {...{ onChangeContent }}
                />
              ),
            }}
            error={!!errors.content?.message}
            helperText={errors.content?.message}
          />

          <div>
            <Grid container spacing={{ xs: 1, md: 2 }}>
              <Grid item xs={6} md={3}>
                <Input
                  fullWidth
                  placeholder={t("full_name")}
                  {...register("customer_name", {
                    required: t("requied_input"),
                  })}
                  error={!!errors.customer_name?.message}
                  helperText={errors.customer_name?.message}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <EmailInput
                  fullWidth
                  className="email-input"
                  placeholder="olivia@untitledui.com"
                  {...register("email", {
                    required: t("requied_input"),
                  })}
                  error={!!errors.email?.message}
                  helperText={errors.email?.message}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <FormControl
                  error={!!errors?.country}
                  className={phoneCountryCode ? "" : "placeholder"}
                  fullWidth
                >
                  <Controller
                    name="country"
                    control={control}
                    rules={{
                      required: t("requied_input"),
                    }}
                    defaultValue={phoneCountryCode}
                    render={({ field }) => (
                      <Select {...field} options={countries} />
                    )}
                  />
                  {errors.country && (
                    <FormHelperText error>
                      {errors.country.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={6} md={3}>
                <Input
                  fullWidth
                  {...register("phone_number", {
                    required: t("requied_input"),
                  })}
                  placeholder={t("phone_number")}
                  error={!!errors.phone_number?.message}
                  helperText={errors.phone_number?.message}
                />
              </Grid>
            </Grid>
          </div>

          <Row className="term-box" spacing={1}>
            <Controller
              name="auto_inquiry"
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={field.value} />
              )}
            />
            <XsText color={COLORS.gray[500]}>
              {t("send_inquiries_with_freshdi_ai", {
                s: (code: ReactNode) => <span>{code}</span>,
              })}
            </XsText>
          </Row>

          <Row className="term-box" spacing={1}>
            <Checkbox
              checked={acceptTerm}
              onChange={(e) => setAcceptTerm(e.target.checked)}
            />
            <XsText color={COLORS.gray[500]} className="term-text">
              {t("send_inquiry_term_2", {
                s: (code: ReactNode) => (
                  <OpenTabLink href={PATHS.termCondition}>{code}</OpenTabLink>
                ),
                ss: (code: ReactNode) => (
                  <OpenTabLink href={PATHS.policy}>{code}</OpenTabLink>
                ),
                sss: (code: ReactNode) => (
                  <OpenTabLink href={PATHS.disclaimers}>{code}</OpenTabLink>
                ),
              })}
            </XsText>
          </Row>

          <Row justifyContent={{ xs: "flex-start", md: "center" }}>
            <GreenButton type="submit" disabled={loading || !acceptTerm}>
              {t("inquiry_now")}
            </GreenButton>

            <OpenTabLink href={PATHS.postRfq}>
              <Row spacing={1} className="post-rfq-text">
                <Img src={APP_ICONS.postRfq} />
                <SmText color={COLORS.yellow[500]}>
                  {t("post_request_quotation")}
                </SmText>
              </Row>
            </OpenTabLink>
          </Row>
        </StyledStack>
      </Stack>

      <Dialog
        open={successful}
        onClose={onCloseSuccessful}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: !isLoggedIn && activateKey ? 678 : 1048,
          },
          id: "finish-inquiry-form",
        }}
      >
        {!isLoggedIn && activateKey ? (
          <JoinUsForm
            {...{ activateKey }}
            onClose={onCloseSuccessful}
            getData={getValues}
            typeIds={{ inquiryId }}
            category={{
              value: itemData?.product_category || "",
              label: itemData?.product_category_name || "",
            }}
          />
        ) : (
          <FinishInquiryForm
            onClose={onCloseSuccessful}
            {...{ itemData, oldContent }}
            getInquiryData={getValues}
          />
        )}
      </Dialog>

      {/* Block spam dialog */}
      <Dialog
        open={blockDialog.open}
        onClose={onCloseBlock}
        PaperProps={{ sx: { width: "100%", maxWidth: 472 } }}
      >
        <BlockSpamInquiryPopup onClose={onCloseBlock} type={blockDialog.type} />
      </Dialog>
    </Fragment>
  );
};

export default SendInquiryForm;
